import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { Form, FormElement } from '@progress/kendo-react-form';
import { useLocalization } from '@progress/kendo-react-intl';
import { TabStrip, TabStripSelectEventArguments, TabStripTab } from '@progress/kendo-react-layout';
import { useQuery, useQueryClient } from 'react-query';
import { useAnomalyBandContext } from './AnomalyBand/AnomalyBandContext';
import type { AnomalyBandWithForm } from './AnomalyBand/types';
import {
  buildAnomalyPayload,
  buildCreateAnchorPayload,
  buildUpdateAnomalyPayload,
  buildUpdateAnchorPayload,
  ensureOptions,
  getLocalizedCriticalityOptions,
  getInitialValuesFromBand,
  mergeOptions,
  OptionItem,
  toMultiValues,
  toOptions,
  toUnixSeconds,
} from './AnomalyDetectorForm.utils';
import anomalyApiService from '../../../../Utilities/AnomalyApiService';
import { Button } from '@progress/kendo-react-buttons';
import { enMessages } from '../../../../../../../messages/en-US';
import AnomalyDetailsForm from './AnomalyDetailsForm';
import AnomalyMlResults from './AnomalyMlResults';
import AnomalyNotesTab from './AnomalyNotesTab';

interface AnomalyDetectorFormProps {
  assetId: string;
  endpointId: string;
  onClose: () => void;
  /** @deprecated Ignored; layout is flex-based. Kept so older call sites still type-check. */
  containerHeight?: number;
  userId?: string;
  userName?: string;
  chartTabContent?: React.ReactNode;
  borderlessContent?: boolean;
  closeOnSave?: boolean;
  detailsReadOnly?: boolean;
  onEdit?: () => void;
  onSaveSuccess?: () => void;
}

export default function AnomalyDetectorForm({
  assetId,
  endpointId,
  onClose,
  containerHeight: _containerHeight,
  userId,
  userName,
  chartTabContent,
  borderlessContent = false,
  closeOnSave = true,
  detailsReadOnly = false,
  onEdit,
  onSaveSuccess,
}: AnomalyDetectorFormProps): React.ReactElement | null {
  const ctx = useAnomalyBandContext();
  const localizationService = useLocalization();
  const queryClient = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [formValid, setFormValid] = useState(false);
  const [selectedPanelIndex, setSelectedPanelIndex] = useState(0);
  const [indicatorOptions, setIndicatorOptions] = useState<OptionItem[]>([]);
  const [rootCauseOptions, setRootCauseOptions] = useState<OptionItem[]>([]);
  const [actionOptions, setActionOptions] = useState<OptionItem[]>([]);
  const criticalityOptions = useMemo(
    () => getLocalizedCriticalityOptions(localizationService),
    [localizationService]
  );
  const anomalyLookupsQuery = useQuery(
    ['anomalyLookups'],
    () => anomalyApiService.getAnomalyLookups(),
    {
      staleTime: 0,
      refetchOnMount: 'always',
    }
  );

  const bands = ctx?.bands ?? [];
  const selectedBandId = ctx?.selectedBandId ?? null;
  const selectedBand = bands.find((b) => b.id === selectedBandId) as AnomalyBandWithForm | undefined;
  const persistableBands = bands;
  const isAnchorBand = selectedBand?.bandType === 'anchor';

  useEffect(() => {
    setSelectedPanelIndex(0);
  }, [selectedBandId]);

  useEffect(() => {
    if (!anomalyLookupsQuery.data) return;

    const indicatorLookupOptions = anomalyLookupsQuery.data.indicators;
    const rootCauseLookupOptions = anomalyLookupsQuery.data.rootCauses;
    const actionLookupOptions = anomalyLookupsQuery.data.actions;

    if (indicatorLookupOptions.length > 0) {
      setIndicatorOptions((prev) => mergeOptions(prev, indicatorLookupOptions));
    }

    if (rootCauseLookupOptions.length > 0) {
      setRootCauseOptions((prev) => mergeOptions(prev, rootCauseLookupOptions));
    }

    if (actionLookupOptions.length > 0) {
      setActionOptions((prev) => mergeOptions(prev, actionLookupOptions));
    }
  }, [anomalyLookupsQuery.data]);

  useEffect(() => {
    if (!selectedBand) return;

    setIndicatorOptions((prev) => ensureOptions(prev, selectedBand.indicators));
    setRootCauseOptions((prev) => ensureOptions(prev, selectedBand.rootCauses));
    setActionOptions((prev) => ensureOptions(prev, selectedBand.actions));
  }, [selectedBand]);

  const hasSelectedBand = !!selectedBandId;
  const canShowNotes = hasSelectedBand;
  const isNewAnomaly = !!selectedBandId && !selectedBand?.persistedId;
  const isEditingSelectedBand = !!selectedBand?.persistedId;
  const highestNonMlTabIndex = chartTabContent ? (canShowNotes ? 2 : 1) : canShowNotes ? 1 : 0;
  const canShowMlResults =
    hasSelectedBand &&
    !isAnchorBand &&
    !isNewAnomaly &&
    (selectedBand?.mlResults?.length ?? 0) > 0;

  useEffect(() => {
    if (!canShowMlResults && selectedPanelIndex > highestNonMlTabIndex) {
      setSelectedPanelIndex(0);
    }
  }, [canShowMlResults, highestNonMlTabIndex, selectedPanelIndex]);

  if (!ctx) return null;

  const { pendingDeleteBandId, setPendingDeleteBandId } = ctx;
  const showDeleteConfirmation = pendingDeleteBandId === selectedBandId && !!selectedBand?.persistedId;
  const deleteTitle = isAnchorBand
    ? localizationService.toLanguageString('custom.deleteSelectedAnchor', enMessages.custom.deleteSelectedAnchor)
    : localizationService.toLanguageString('custom.deleteSelectedAnomaly', enMessages.custom.deleteSelectedAnomaly);
  const deleteMessage = isAnchorBand
    ? localizationService.toLanguageString(
        'custom.deleteSelectedAnchorConfirmation',
        enMessages.custom.deleteSelectedAnchorConfirmation
      )
    : localizationService.toLanguageString(
        'custom.deleteSelectedAnomalyConfirmation',
        enMessages.custom.deleteSelectedAnomalyConfirmation
      );

  const getInitialValues = () =>
    getInitialValuesFromBand({
      selectedBand,
      criticalityOptions,
      indicatorOptions,
      rootCauseOptions,
      actionOptions,
    });

  const syncFormToBands = (data: any) => {
    if (!selectedBandId || !ctx) return;
    const streamArr = Array.isArray(data.streams) ? data.streams : [];
    const streams = streamArr.map((item: { value?: string }) =>
      typeof item === 'object' && item?.value != null ? item.value : item
    );
    const startDateTime =
      data.startDateTime instanceof Date && !Number.isNaN(data.startDateTime.getTime())
        ? data.startDateTime
        : undefined;
    const endDateTime =
      data.endDateTime instanceof Date && !Number.isNaN(data.endDateTime.getTime())
        ? data.endDateTime
        : undefined;
    const hasValidPeriod =
      startDateTime != null &&
      endDateTime != null &&
      startDateTime.getTime() <= endDateTime.getTime();

    ctx.setBands((prev) =>
      prev.map((b) =>
        b.id === selectedBandId
          ? {
              ...b,
              ...(hasValidPeriod
                ? {
                    startTime: toUnixSeconds(startDateTime),
                    endTime: toUnixSeconds(endDateTime),
                  }
                : {}),
              ...(b.bandType === 'anchor'
                ? {
                    streams,
                  }
                : {
                    streams,
                    criticality: data.criticality?.value ?? data.criticality,
                    indicators: toMultiValues(data.indicators),
                    rootCauses: toMultiValues(data.rootCauses),
                    actions: toMultiValues(data.actions),
                    confidence: data.confidence?.value ?? data.confidence,
                    savings: data.savings != null ? Number(data.savings) : undefined,
                    ongoing: data.ongoing === true,
                  }),
              userNotes: data.userNotes ?? '',
            }
          : b
      )
    );
  };

  const handleSave = async () => {
    if (persistableBands.length === 0) return;
    setIsSaving(true);
    try {
      for (const band of persistableBands) {
        if (band.bandType === 'anchor') {
          if (band.persistedId) {
            const payload = buildUpdateAnchorPayload({
              band,
              assetId,
              endpointId,
              userName,
              userId,
            });
            await anomalyApiService.updateAnchor(payload, band.persistedId);
          } else {
            const payload = buildCreateAnchorPayload({
              band,
              assetId,
              endpointId,
              userName,
              userId,
            });
            await anomalyApiService.createAnchor(payload);
          }

          continue;
        }

        if (band.persistedId) {
          const payload = buildUpdateAnomalyPayload({
            band,
            assetId,
            endpointId,
            userName,
            userId,
          });
          await anomalyApiService.updateAnomaly(payload, band.persistedId);
        } else {
          const payload = buildAnomalyPayload({
            band,
            assetId,
            endpointId,
            userName,
            userId,
          });
          await anomalyApiService.createAnomaly(payload);
        }
      }
      await queryClient.invalidateQueries(['endpointAnomalyBands', endpointId]);
      await queryClient.invalidateQueries(['pointAnomalyBands', endpointId]);
      await queryClient.invalidateQueries(['pointInsightsAnomalies', endpointId]);
      if (assetId) {
        await queryClient.invalidateQueries(['assetInsightsAnomalies', assetId]);
      }
      if (closeOnSave) {
        onClose();
      }
      onSaveSuccess?.();
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  const handleDeleteConfirmed = async () => {
    if (!selectedBandId || !selectedBand?.persistedId) return;

    setIsDeleting(true);
    try {
      if (selectedBand.bandType === 'anchor') {
        await anomalyApiService.deleteAnchor(selectedBand.persistedId);
      } else {
        await anomalyApiService.deleteAnomaly(selectedBand.persistedId);
      }

      ctx.setBands((prev) => prev.filter((band) => band.id !== selectedBandId));
      ctx.setSelectedBandId(null);
      setPendingDeleteBandId(null);
      // Optimistically drop the band from the chart's cache so it disappears
      // immediately, instead of flickering until the invalidation refetch lands.
      const deletedPersistedId = selectedBand.persistedId;
      queryClient.setQueryData<AnomalyBandWithForm[]>(
        ['endpointAnomalyBands', endpointId],
        (old) => (old ? old.filter((b) => b.persistedId !== deletedPersistedId) : [])
      );
      await queryClient.invalidateQueries(['endpointAnomalyBands', endpointId]);
      await queryClient.invalidateQueries(['pointAnomalyBands', endpointId]);
      await queryClient.invalidateQueries(['pointInsightsAnomalies', endpointId]);
      if (assetId) {
        await queryClient.invalidateQueries(['assetInsightsAnomalies', assetId]);
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancelled = () => {
    setPendingDeleteBandId(null);
  };

  const formatPeriod = (unixSeconds: number) =>
    moment.unix(unixSeconds).format('DD MMM YYYY HH:mm');

  if (showDeleteConfirmation) {
    return (
      <div className="k-d-flex k-flex-col k-padding-md" style={{ height: '100%' }}>
        <div className="flex flex-1 items-center justify-center px-6 py-10 text-center" style={{ minHeight: 0 }}>
          <div className="max-w-sm">
            <div className="text-base font-medium text-gray-900">
              {deleteTitle}
            </div>
            <div className="mt-2 text-sm text-gray-500">
              {deleteMessage}
            </div>
            <div className="mt-6 flex justify-center gap-2">
              <Button type="button" themeColor="base" onClick={handleDeleteCancelled} disabled={isDeleting}>
                {localizationService.toLanguageString('custom.cancel', enMessages.custom.cancel)}
              </Button>
              <Button type="button" themeColor="primary" onClick={handleDeleteConfirmed} disabled={isDeleting}>
                {isDeleting
                  ? localizationService.toLanguageString('custom.deleting', enMessages.custom.deleting)
                  : localizationService.toLanguageString('custom.delete', enMessages.custom.delete)}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const addIndicator = (item: OptionItem) => {
    setIndicatorOptions((prev) => (prev.some((o) => o.value === item.value) ? prev : [...prev, item]));
  };
  const addRootCause = (item: OptionItem) => {
    setRootCauseOptions((prev) => (prev.some((o) => o.value === item.value) ? prev : [...prev, item]));
  };
  const addAction = (item: OptionItem) => {
    setActionOptions((prev) => (prev.some((o) => o.value === item.value) ? prev : [...prev, item]));
  };
  const handlePanelSelect = (event: TabStripSelectEventArguments) => {
    setSelectedPanelIndex(event.selected);
  };
  const saveButtonLabel = isEditingSelectedBand
    ? localizationService.toLanguageString('custom.update', enMessages.custom.update)
    : localizationService.toLanguageString('custom.add', enMessages.custom.add);
  const notesTabIndex = chartTabContent ? 2 : 1;
  const shouldShowEditButton = !!onEdit && detailsReadOnly && hasSelectedBand && selectedPanelIndex !== notesTabIndex;
  const shouldShowFooter =
    hasSelectedBand &&
    !detailsReadOnly &&
    !(isEditingSelectedBand && selectedPanelIndex === notesTabIndex);

  return (
    <div className="k-d-flex k-flex-col k-padding-md" style={{ height: '100%' }}>
      <div className="k-d-flex k-flex-col k-flex-1 [&_.k-form]:flex [&_.k-form]:min-h-0 [&_.k-form]:flex-1 [&_.k-form]:flex-col" style={{ minHeight: 0 }}>
        <Form
          key={selectedBandId ?? 'no-selection'}
          onSubmit={() => undefined}
          initialValues={getInitialValues()}
          render={(formRenderProps) => (
            <FormElement className="flex min-h-0 flex-1 flex-col overflow-hidden">
              <div
                className={`flex min-h-0 flex-1 flex-col overflow-hidden [&_.k-tabstrip]:flex [&_.k-tabstrip]:min-h-0 [&_.k-tabstrip]:flex-1 [&_.k-tabstrip]:flex-col [&_.k-tabstrip-content]:flex [&_.k-tabstrip-content]:min-h-0 [&_.k-tabstrip-content]:flex-1 [&_.k-tabstrip-content]:overflow-hidden [&_.k-content]:flex [&_.k-content]:min-h-0 [&_.k-content]:flex-1 [&_.k-content]:flex-col [&_.k-content]:overflow-hidden [&_.k-animation-container]:flex [&_.k-animation-container]:min-h-0 [&_.k-animation-container]:flex-1 [&_.k-animation-container]:flex-col [&_.k-animation-container]:overflow-hidden [&_.k-child-animation-container]:flex [&_.k-child-animation-container]:min-h-0 [&_.k-child-animation-container]:flex-1 [&_.k-child-animation-container]:flex-col [&_.k-child-animation-container]:overflow-hidden [&_[role='tabpanel']]:flex [&_[role='tabpanel']]:min-h-0 [&_[role='tabpanel']]:flex-1 [&_[role='tabpanel']]:flex-col [&_[role='tabpanel']]:overflow-hidden [&_.k-tabstrip-items-wrapper]:shrink-0 ${
                  borderlessContent
                    ? '[&_.k-tabstrip]:border-0 [&_.k-tabstrip-content]:border-0 [&_.k-content]:border-0'
                    : ''
                }`}
              >
                <TabStrip
                  selected={selectedPanelIndex}
                  onSelect={handlePanelSelect}
                  keepTabsMounted={true}
                  className="anomaly-detail-tabstrip"
                >
                  {chartTabContent && (
                    <TabStripTab title={localizationService.toLanguageString('custom.chart', enMessages.custom.chart)}>
                      <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden pt-2">
                        {chartTabContent}
                      </div>
                    </TabStripTab>
                  )}
                  <TabStripTab title={localizationService.toLanguageString('custom.details', enMessages.custom.details)}>
                    <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden">
                      <AnomalyDetailsForm
                        formRenderProps={formRenderProps}
                        selectedBandId={selectedBandId}
                        selectedBand={selectedBand}
                        readOnly={detailsReadOnly}
                        formatPeriod={formatPeriod}
                        syncFormToBands={syncFormToBands}
                        localizationService={localizationService}
                        onValidityChange={setFormValid}
                        criticalityOptions={criticalityOptions}
                        indicatorOptions={indicatorOptions}
                        rootCauseOptions={rootCauseOptions}
                        actionOptions={actionOptions}
                        onIndicatorCustomAdded={addIndicator}
                        onRootCauseCustomAdded={addRootCause}
                        onActionCustomAdded={addAction}
                      />
                    </div>
                  </TabStripTab>
                  {canShowNotes && (
                    <TabStripTab title={localizationService.toLanguageString('custom.notes', enMessages.custom.notes)}>
                      <div className="flex min-h-0 flex-1 flex-col overflow-hidden pt-2">
                        <AnomalyNotesTab
                          assetId={assetId}
                          endpointId={endpointId}
                          selectedBandId={selectedBandId}
                          selectedBand={selectedBand}
                          userName={userName}
                        />
                      </div>
                    </TabStripTab>
                  )}
                  {canShowMlResults && (
                    <TabStripTab title={localizationService.toLanguageString('custom.mlResults', enMessages.custom.mlResults)}>
                      <div className="flex min-h-0 flex-1 flex-col overflow-auto pt-2">
                        <AnomalyMlResults selectedBand={selectedBand} />
                      </div>
                    </TabStripTab>
                  )}
                </TabStrip>
              </div>
            </FormElement>
          )}
        />
      </div>
      {shouldShowFooter && (
        <div className="flex shrink-0 justify-end k-form-button">
          <div className="p-2">
            <Button type="button" themeColor="base" onClick={handleCancel}>
              {localizationService.toLanguageString('custom.cancel', enMessages.custom.cancel)}
            </Button>
            <Button type="button" themeColor="primary" onClick={handleSave} disabled={persistableBands.length === 0 || isSaving || isDeleting || !formValid || !selectedBandId || showDeleteConfirmation} style={{ marginLeft: '5px' }}>
              {isSaving
                ? localizationService.toLanguageString('custom.saving', enMessages.custom.saving)
                : saveButtonLabel}
            </Button>
          </div>
        </div>
      )}
      {shouldShowEditButton && (
        <div className="flex shrink-0 justify-end k-form-button">
          <div className="p-2">
            {!!selectedBand?.persistedId && (
              <Button type="button" themeColor="base" onClick={() => setPendingDeleteBandId(selectedBandId!)}>
                {localizationService.toLanguageString('custom.delete', enMessages.custom.delete)}
              </Button>
            )}
            <Button type="button" themeColor="primary" onClick={onEdit} style={{ marginLeft: '5px' }}>
              {localizationService.toLanguageString('custom.edit', enMessages.custom.edit)}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
